import { useIsDarkMode } from '@psl/appComponents/UserInterfaceIdiomContext';
import { Center } from '@psl/components/Center';
import { DefaultLoadingIndicator } from '@psl/components/LoadingIndicator/DefaultLoadingIndicator';
import {
  createContext,
  forwardRef,
  memo,
  ReactNode,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  KeyboardAvoidingView,
  StyleProp,
  StyleSheet,
  View,
  ViewStyle,
} from 'react-native';
import { useCreation } from '../../functions/hooks/useCreation';
import { makeDebug } from '../../utils/makeDebug';
import { FCC } from '../../utils/react';

const debug = makeDebug('loading-indicator');

interface LoadingContextValue {
  showLoading(compId: any): void;
  hideLoading(compId: any): void;
  isVisible(compId: any): boolean;
}

const LoadingContext = createContext<LoadingContextValue>({
  showLoading() {},
  hideLoading() {},
  isVisible() {
    return false;
  },
});

export const LoadingIndicatorProvider: FCC<{
  loadingIndicator: ReactNode;
}> = props => {
  const [, setRefreshTime] = useState(0);

  const loadingVisibleCompIds = useCreation(() => new Set(), []);

  const ctxValue: LoadingContextValue = useMemo(
    () => ({
      showLoading(compId) {
        loadingVisibleCompIds.add(compId);
        setRefreshTime(Date.now());
      },
      hideLoading(compId) {
        loadingVisibleCompIds.delete(compId);
        setRefreshTime(Date.now());
      },
      isVisible(compId): boolean {
        return loadingVisibleCompIds.has(compId);
      },
    }),
    [loadingVisibleCompIds],
  );

  const children = (
    <LoadingContext.Provider value={ctxValue}>
      {props.children}
    </LoadingContext.Provider>
  );

  const visible = loadingVisibleCompIds.size > 0;
  if (!visible) return <>{children}</>;

  return (
    <>
      {children}
      <KeyboardAvoidingView
        style={StyleSheet.absoluteFill}
        css={{ flex: 1, justifyContent: 'center' }}
      >
        {props.loadingIndicator}
      </KeyboardAvoidingView>
    </>
  );
};

export interface LoadingController {
  show(): void;
  hide(): void;
}

export const useLoading: () => LoadingController = () => {
  const compId = useRef<void>();

  const ctx = useContext(LoadingContext);

  const ctl = useMemo(
    () => ({
      show: () => ctx.showLoading(compId),
      hide: () => ctx.hideLoading(compId),
    }),
    [ctx],
  );

  useEffect(
    () => () => {
      if (ctx.isVisible(compId)) {
        debug(`[useLoading] component ${compId.current} is still visible.`);

        ctx.hideLoading(compId);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return ctl;
};

export const GlobalLoading = memo(() => {
  const loading = useLoading();
  useEffect(() => {
    loading.show();
    return () => loading.hide();
  }, [loading]);
  return null;
});

export type Spin = {
  show: () => void;
  hide: () => void;
};

type SpinProps = {
  spinning?: boolean;
  nativeID?: string;
  size?: 'small' | 'large' | 'default';
  style?: StyleProp<ViewStyle>;
  spinnerStyle?: StyleProp<ViewStyle>;
  backdropStyle?: StyleProp<ViewStyle>;
  children?: ReactNode;
};

export const Spin = memo(
  forwardRef<Spin, SpinProps>((props, ref) => {
    const isDarkMode = useIsDarkMode();
    const [spinning, setSpinning] = useState(props.spinning ?? false);

    const backdropStyle = useMemo<ViewStyle>(
      () => ({
        backgroundColor: isDarkMode
          ? 'rgba(0, 0, 0, 0.25)'
          : 'rgba(255, 255, 255, 0.5)',
      }),
      [isDarkMode],
    );

    useImperativeHandle(ref, () => ({
      show: () => {
        setSpinning(true);
      },
      hide: () => {
        setSpinning(false);
      },
    }));

    useEffect(() => {
      if (props.spinning !== undefined) {
        setSpinning(props.spinning);
      }
    }, [props.spinning]);

    if (props.children) {
      return (
        <View style={props.style} nativeID={props.nativeID}>
          {props.children}
          {spinning && (
            <Center
              style={[
                StyleSheet.absoluteFill,
                backdropStyle,
                props.backdropStyle,
              ]}
            >
              <DefaultLoadingIndicator
                size={props.size}
                style={props.spinnerStyle}
                css={`
                  background-color: black;
                `}
              />
            </Center>
          )}
        </View>
      );
    }

    if (!spinning) return null;

    return (
      <Center
        style={[
          StyleSheet.absoluteFill,
          props.style,
          backdropStyle,
          props.backdropStyle,
        ]}
        nativeID={props.nativeID}
      >
        <DefaultLoadingIndicator
          size={props.size}
          style={props.spinnerStyle}
          css={`
            background-color: black;
          `}
        />
      </Center>
    );
  }),
);
